import type { TFunction } from "i18next"
import { RefreshCwIcon } from "lucide-react"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { formatMoney } from "@/helpers/format"
import { DateHelper } from "@/utils"

import { getTransactionStatusLabel } from "./transaction-status"

type UpdatedTransactionType = {
  id: string
  status: string
  oldAmount?: number
  oldDueDate?: string
  newAmount?: number
  newDueDate?: string
  processorInformation?: {
    name?: string
    tid?: string
    message?: string
    returnCode?: string
  }
  createdAt: string
  updatedAt: string
}

interface UpdatedTransactionsInfoProps {
  updatedTransactions?: UpdatedTransactionType[]
  t: TFunction
}

const getUpdateStatusVariant = (status?: string) => {
  switch (status?.toUpperCase()) {
    case "UPDATED":
    case "SUCCESS":
      return "default"
    case "PENDING":
    case "PROCESSING":
      return "secondary"
    case "FAILED":
      return "destructive"
    default:
      return "outline"
  }
}

export const UpdatedTransactionsInfo = ({
  updatedTransactions,
  t,
}: UpdatedTransactionsInfoProps) => {
  const translationKey = "orders.transactionsTable.updates"

  if (
    !updatedTransactions ||
    updatedTransactions.length === 0
  ) {
    return null
  }

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1.5 border-blue-300 bg-blue-50 text-xs text-blue-700 hover:bg-blue-100"
        >
          <RefreshCwIcon size={12} />
          {updatedTransactions.length === 1
            ? t(`${translationKey}.single`)
            : t(`${translationKey}.multiple`, {
                count: updatedTransactions.length,
              })}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-0" align="start">
        <div className="border-b bg-muted/50 px-4 py-3">
          <div className="flex items-center justify-between">
            <h4 className="flex items-center gap-2 text-sm font-semibold">
              <RefreshCwIcon size={14} />
              {t(`${translationKey}.title`)}
            </h4>
            <Badge variant="secondary">
              {updatedTransactions.length}
            </Badge>
          </div>
        </div>
        <div className="max-h-[300px] overflow-auto">
          {updatedTransactions.map((update, index) => (
            <div
              key={update.id}
              className={`space-y-2 p-4 ${
                index < updatedTransactions.length - 1
                  ? "border-b"
                  : ""
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">
                  {DateHelper.format(
                    update.createdAt,
                    "dd/MM/yyyy HH:mm:ss"
                  )}
                </span>
                <Badge
                  variant={getUpdateStatusVariant(
                    update.status
                  )}
                >
                  {getTransactionStatusLabel(
                    update.status,
                    t
                  )}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                {(update.oldAmount !== undefined ||
                  update.newAmount !== undefined) && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t(`${translationKey}.amount`)}
                    </span>
                    <div className="flex items-center gap-1">
                      {update.oldAmount !== undefined && (
                        <span className="text-muted-foreground line-through">
                          {formatMoney(update.oldAmount)}
                        </span>
                      )}
                      {update.newAmount !== undefined && (
                        <>
                          <span className="text-muted-foreground">
                            →
                          </span>
                          <span className="font-semibold text-blue-600">
                            {formatMoney(update.newAmount)}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {(update.oldDueDate ||
                  update.newDueDate) && (
                  <div className="flex flex-col gap-0.5">
                    <span className="text-xs font-medium text-muted-foreground">
                      {t(`${translationKey}.dueDate`)}
                    </span>
                    <div className="flex items-center gap-1">
                      {update.oldDueDate && (
                        <span className="text-muted-foreground line-through">
                          {DateHelper.format(
                            update.oldDueDate,
                            "dd/MM/yyyy"
                          )}
                        </span>
                      )}
                      {update.newDueDate && (
                        <>
                          <span className="text-muted-foreground">
                            →
                          </span>
                          <span className="font-semibold text-blue-600">
                            {DateHelper.format(
                              update.newDueDate,
                              "dd/MM/yyyy"
                            )}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {update.processorInformation &&
                (update.processorInformation.name ||
                  update.processorInformation.tid ||
                  update.processorInformation.message ||
                  update.processorInformation
                    .returnCode) && (
                  <div className="rounded-md border bg-muted/30 p-2">
                    <span className="mb-1 block text-xs font-medium text-muted-foreground">
                      {t(
                        "orders.transactionsTable.processorInfo.title"
                      )}
                    </span>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      {update.processorInformation.name && (
                        <div>
                          <span className="text-muted-foreground">
                            {t(
                              "orders.transactionsTable.processorInfo.processor"
                            )}
                            :{" "}
                          </span>
                          {update.processorInformation.name}
                        </div>
                      )}
                      {update.processorInformation.tid && (
                        <div className="flex flex-col gap-0.5">
                          <span className="text-muted-foreground">
                            TID
                          </span>
                          <div className="flex items-center gap-1">
                            <code className="max-w-[100px] truncate rounded bg-muted px-1.5 py-0.5 text-xs">
                              {
                                update.processorInformation
                                  .tid
                              }
                            </code>
                            <ClipboardButton
                              text={
                                update.processorInformation
                                  .tid
                              }
                              variant="ghost"
                              className="h-4 w-4 shrink-0"
                            />
                          </div>
                        </div>
                      )}
                      {update.processorInformation
                        .returnCode && (
                        <div>
                          <span className="text-muted-foreground">
                            {t(
                              "orders.transactionsTable.processorInfo.returnCode"
                            )}
                            :{" "}
                          </span>
                          {
                            update.processorInformation
                              .returnCode
                          }
                        </div>
                      )}
                    </div>
                    {update.processorInformation
                      .message && (
                      <div className="mt-1 text-xs">
                        <span className="text-muted-foreground">
                          {t(
                            "orders.transactionsTable.processorInfo.message"
                          )}
                          :{" "}
                        </span>
                        {
                          update.processorInformation
                            .message
                        }
                      </div>
                    )}
                  </div>
                )}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}
